"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { OctagonAlert as AlertOctagon, TriangleAlert as AlertTriangle, ArrowRight, Droplets, Flame, PhoneCall, ShieldAlert, Waves, Wrench } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const emergencyProblemsList = [{
  id: 1,
  title: "Burst Pipe",
  description: "Water spraying or pooling rapidly behind drywall, ceilings, or under foundation slabs.",
  urgencyLevel: "Critical - Immediate Shut-Off",
  iconName: "burst"
}, {
  id: 2,
  title: "Major Water Leak",
  description: "Uncontrolled water escape endangering structural framing, wooden flooring, and electrical runs.",
  urgencyLevel: "Urgent Response Needed",
  iconName: "leak"
}, {
  id: 3,
  title: "Sewer Backup",
  description: "Toxic, foul-smelling wastewater pushing up into bathtubs, sinks, or floor drains.",
  urgencyLevel: "Severe Biohazard",
  iconName: "sewer"
}, {
  id: 4,
  title: "Overflowing Toilet",
  description: "Continuous bowl flooding failing to halt despite shut-off valve manipulation.",
  urgencyLevel: "High Damage Risk",
  iconName: "toilet"
}, {
  id: 5,
  title: "Water Heater Leak",
  description: "Ruptured tank vessel or boiling-line failure flooding the basement, closet, or attic.",
  urgencyLevel: "Scalding & Flood Hazard",
  iconName: "heater"
}, {
  id: 6,
  title: "Active Flooding",
  description: "Standing water accumulating across living areas, crawlspaces, or utility rooms.",
  urgencyLevel: "Emergency Dispatch",
  iconName: "flood"
}];
function ProblemIcon({
  name
}: {
  name: string;
}) {
  const iconClass = "size-6 shrink-0";
  switch (name) {
    case "burst":
      return <Wrench className={iconClass} />;
    case "leak":
      return <Droplets className={iconClass} />;
    case "sewer":
      return <AlertTriangle className={iconClass} />;
    case "toilet":
      return <Waves className={iconClass} />;
    case "heater":
      return <Flame className={iconClass} />;
    case "flood":
      return <AlertOctagon className={iconClass} />;
    default:
      return <ShieldAlert className={iconClass} />;
  }
}
export default function EmergencyProblems() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="emergencyproblems" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border relative overflow-hidden">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Centered Header Section */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 mb-4">
            <Badge variant="destructive" className="px-3 py-1 font-semibold tracking-wide uppercase text-xs shadow-xs">
              <ShieldAlert className="size-3.5 mr-1" />
              Urgent Triage
            </Badge>
          </div>

          <h2 className="font-default text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-4">
            Is Your Plumbing Problem Causing Active Damage?
          </h2>

          <p className="font-default text-base md:text-lg text-muted-foreground leading-relaxed">
            Jonesboro Emergency Plumber provides 24/7 on-demand local dispatch
            across Jonesboro, GA. If you see any of the critical issues below,
            take action right away to protect your property.
          </p>
        </div>

        {/* 6-Item Emergency Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12 md:mb-16">
          {emergencyProblemsList.map((item, i) => <motion.div key={item.id} data-index={i} initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: i * 0.08
        }} className="h-full">
              <Card className="h-full border border-border bg-card hover:border-destructive/40 transition-colors shadow-sm flex flex-col justify-between">
                <div>
                  <CardHeader className="flex flex-row items-start justify-between gap-4 pb-3">
                    <div className="p-3 rounded-lg bg-destructive/10 text-destructive border border-destructive/20">
                      <ProblemIcon name={item.iconName} />
                    </div>
                    <Badge variant="outline" className="border-destructive/30 text-destructive bg-destructive/5 text-xs font-semibold">
                      {item.urgencyLevel}
                    </Badge>
                  </CardHeader>

                  <CardContent className="pt-2">
                    <CardTitle className="font-default text-xl font-bold text-card-foreground mb-2">
                      {item.title}
                    </CardTitle>
                    <p className="font-default text-sm text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                  </CardContent>
                </div>

                <div className="px-6 pb-6 pt-2">
                  <div className="pt-4 border-t border-border/60 flex items-center justify-between text-xs font-medium text-destructive">
                    <span>Direct Emergency Dispatch</span>
                    <span>24/7 Available</span>
                  </div>
                </div>
              </Card>
            </motion.div>)}
        </div>

        {/* Emergency Dual CTA Response Block */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 15
      }} whileInView={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="bg-primary text-primary-foreground rounded-2xl p-8 md:p-10 shadow-xl border border-primary/20 text-center md:text-left">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-primary-foreground/10 text-primary-foreground text-xs font-medium mb-3">
                <span className="size-2 rounded-full bg-accent animate-pulse" />
                Live Dispatchers On Standby
              </div>
              <h3 className="font-default text-2xl sm:text-3xl font-bold text-primary-foreground mb-2">
                Need Immediate Help In Jonesboro?
              </h3>
              <p className="font-default text-sm sm:text-base text-primary-foreground/85 leading-relaxed">
                Do not wait for water to soak through ceilings or ruin flooring.
                Call our 24/7 emergency line now for rapid arrival.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4 w-full md:w-auto shrink-0">
              <Button size="lg" className="w-full sm:w-auto bg-accent text-accent-foreground hover:bg-accent/90 font-bold px-6 shadow-md transition-colors" asChild>
                <a href="tel:4708479076">
                  <PhoneCall className="mr-2 size-5" />
                  Call (470) 847-9076
                </a>
              </Button>

              <Button size="lg" variant="outline" className="w-full sm:w-auto bg-transparent border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground font-semibold px-6 transition-colors" asChild>
                <Link to="/emergency-plumbing/">
                  View Emergency Services
                  <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}